/**
 * Integrytas Group — StatusBadge (common).
 *
 * Semantic status pill for grids, detail views, facets and exports (P-02).
 * Status is the most-scanned field on any workflow screen — it should never
 * render as plain body text. This badge maps a semantic variant onto the
 * platform's existing status tokens (success/warning/danger/info + neutral),
 * matching YesNoBadge's Acumatica-style pill shape.
 *
 * The DISPLAYED label must already be resolved/localized by the caller
 * (lookup `_tl` display_text, or t('<prefix>.<enum>.<VALUE>') for CHECK
 * enums — §17.B4/B11). Never pass the raw stored code as the label.
 *
 * Pages own the mapping from their status codes to a variant, e.g.:
 *   const VARIANT: Record<string, StatusVariant> = {
 *     DRAFT: 'neutral', SUBMITTED: 'info', RECEIVED: 'success',
 *     PARTIALLY_RECEIVED: 'warning', CANCELLED: 'danger',
 *   };
 *   <StatusBadge variant={VARIANT[row.status_code] ?? 'neutral'} label={row.status_name} />
 */

export type StatusVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const VARIANT_CLS: Record<StatusVariant, string> = {
  neutral: 'border-csm-border bg-csm-chip text-csm-ink-soft',
  info:    'border-csm-info-border bg-csm-info-soft text-csm-info',
  success: 'border-csm-success-border bg-csm-success-soft text-csm-success',
  warning: 'border-csm-warning-border bg-csm-warning-soft text-csm-warning',
  danger:  'border-csm-danger-border bg-csm-danger-soft text-csm-danger',
};

export interface StatusBadgeProps {
  /** Already-localized display text — never the raw stored code. */
  label: string;
  variant?: StatusVariant;
  /** Optional title attribute (e.g. a longer description on hover). */
  title?: string;
}

export function StatusBadge({ label, variant = 'neutral', title }: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={
        'inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-csm-2xs font-semibold tracking-wide ' +
        VARIANT_CLS[variant]
      }
    >
      {label}
    </span>
  );
}
